/* @jsx h */
import { defineComponent, h } from "vue";
import { RestaurantCustomer } from "@lemmonhq/types";

export interface CustomerSearchPanelProps {
  customerSearch: string;
  onCustomerSearch: (value: string) => void;
  isSearching: boolean;
  searchResults: RestaurantCustomer[];
  searchError: string;
  onSelectCustomer: (customer: RestaurantCustomer) => void;
}

export const CustomerSearchPanel = defineComponent<CustomerSearchPanelProps>({
  name: "CustomerSearchPanel",
  props: [
    "customerSearch",
    "onCustomerSearch",
    "isSearching",
    "searchResults",
    "searchError",
    "onSelectCustomer",
  ],
  setup(props) {
    const handleInput = (event: Event) => {
      props.onCustomerSearch((event.target as HTMLInputElement).value);
    };

    const hasTyped = () => props.customerSearch.trim().length >= 2;

    return () => (
      <div class="space-y-4">
        <div class="form-control">
          <label class="label">
            <span class="label-text text-sm font-medium text-base-content/80">
              Search customer
            </span>
            <span class="label-text-alt text-base-content/60">
              Type at least 2 characters
            </span>
          </label>
          <label class="input input-sm md:input-md input-bordered flex items-center gap-2 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <span class="material-symbols-outlined text-base-content/60 text-lg">
              search
            </span>
            <input
              class="grow"
              type="text"
              placeholder="Name or email"
              value={props.customerSearch}
              onInput={handleInput}
            />
            {props.customerSearch.length > 0 && (
              <button
                type="button"
                class="btn btn-xs btn-ghost"
                onClick={() => props.onCustomerSearch("")}
              >
                <span class="material-symbols-outlined text-base">close</span>
              </button>
            )}
          </label>
        </div>

        {props.searchError && (
          <div class="alert alert-error">
            <span class="material-symbols-outlined">error</span>
            {props.searchError}
          </div>
        )}

        <div class="space-y-3">
          {props.isSearching ? (
            <div class="flex items-center justify-center py-8">
              <span class="loading loading-ring loading-lg text-primary" />
            </div>
          ) : props.searchResults.length > 0 ? (
            props.searchResults.map((customer) => (
              <button
                key={customer.id}
                class="card border w-full border-base-300 hover:border-primary/70 hover:shadow-lg transition-all text-left"
                onClick={() => props.onSelectCustomer(customer)}
              >
                <div class="card-body py-4 px-5 gap-2">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="avatar placeholder">
                        <div class="bg-primary/10 text-primary-content rounded-full w-10 h-10 flex items-center justify-center">
                          <span class="text-primary font-semibold">
                            {(customer.name || customer.email || "Unnamed")
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div class="min-w-0">
                        <p class="font-semibold truncate">
                          {customer.name || "Unnamed customer"}
                        </p>
                        <p class="text-xs text-base-content/70 truncate">
                          {customer.email}
                        </p>
                      </div>
                    </div>
                    <span class="badge badge-outline badge-primary">
                      {customer.status_level || "loyalty"}
                    </span>
                  </div>
                  <div class="text-xs text-base-content/60">
                    Joined{" "}
                    {new Date(
                      customer.restaurant_customer_created_at,
                    ).toLocaleDateString()}
                  </div>
                </div>
              </button>
            ))
          ) : hasTyped() ? (
            <div class="rounded-xl border border-dashed border-base-300 bg-base-100/80 p-6 text-center space-y-2">
              <span class="material-symbols-outlined text-3xl text-base-content/40">
                person_search
              </span>
              <p class="text-sm text-base-content/70">
                No customers found. Try a different name or email.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
});
